import { shouldExcludeFromPrefetch } from "@theme/pjax-routing";
import { requestIdleCallback } from "@theme/utilities";

// Warms pjax-max's cache ahead of clicks, by each link's data-prefetch:
//   (none)        when the mouse rests on the link, or it takes keyboard focus (the default)
//   "viewport"    when the link scrolls into view
//   "immediate"   as soon as it's seen
//   "hover"       pjax-max's own: on pointer-over and keyboard focus, at once
//   "critical"    pjax-max's own: once the browser is idle after each page
//   "none"        never
// Pages go through core.prefetch(), so a click on a link still prefetching
// waits for that request instead of sending another, and the cache's bound
// and freshness (pjax-init.js) apply. Without the router (page transitions
// off, or the theme editor) nothing is prefetched, since nothing would use it.
//
// Every page fetched costs Shopify a render and the visitor a download, so by
// default only a link that's about to be clicked is: one the mouse has rested on
// for HOVER_DELAY (a pointer crossing a grid passes over dozens on its way), or
// one that takes keyboard focus. Those count against pjax-max's intent limit, two
// at once with the oldest aborted. A touch screen has no hover, so there a page is
// fetched when its link is tapped.
//
// core.prefetch() can't ask for a low fetch priority, so prefetching starts
// once the page has loaded, and links that scroll into view are fetched when
// the browser is idle: they shouldn't compete with the page's own images.

/** How long the mouse rests on a link before its page is fetched, in ms. */
const HOVER_DELAY = 100;

class LinkPrefetcher {
  #core;
  #observed = new Set();
  #queued = new Set();
  #flushing = false;
  #intersectionObserver;
  /** @type {{ link: HTMLAnchorElement, timer: number } | null} */
  #resting = null;

  constructor(core) {
    this.#core = core;
    this.#intersectionObserver = new IntersectionObserver(this.#onIntersect, {
      rootMargin: "0px",
      threshold: 0,
    });

    this.#scan(document);

    new MutationObserver((mutations) => {
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (node.nodeType === 1) this.#scan(node);
        }
      }
    }).observe(document.body, { subtree: true, childList: true });

    // One delegated listener each, so links added later and inside shadow roots count too
    document.addEventListener("pointerover", this.#onPointerOver);
    document.addEventListener("focusin", this.#onFocusIn);

    // A link observed in a view that has since been swapped out would keep
    // that whole view alive; let them go once a navigation has ended.
    core.addEventListener("NAVIGATE_END", () => {
      for (const link of this.#observed) {
        if (!link.isConnected) this.#unobserve(link);
      }
    });
  }

  #mode(el) {
    return (el.getAttribute("data-prefetch") ?? "").toLowerCase();
  }

  // Only a link with a data-prefetch of its own has anything to set up here.
  #scan(root) {
    const handle = (el) => {
      const mode = this.#mode(el);
      if ((mode !== "immediate" && mode !== "viewport") || !this.#isCandidate(el)) return;
      if (mode === "immediate") {
        this.#core.prefetch(el);
      } else if (!this.#observed.has(el)) {
        this.#observed.add(el);
        this.#intersectionObserver.observe(el);
      }
    };

    if (root.matches?.("a[href][data-prefetch]")) handle(root);
    root.querySelectorAll?.("a[href][data-prefetch]").forEach(handle);
  }

  #isCandidate(el) {
    if (el.target === "_blank") return false;
    if (el.hasAttribute("download") || el.closest("[data-router-disabled]")) return false;
    const raw = el.getAttribute("href") ?? "";
    if (!raw || raw.startsWith("#")) return false;
    if (/^(mailto:|tel:|javascript:)/i.test(raw)) return false;
    if (shouldExcludeFromPrefetch(el.href)) return false;
    return true;
  }

  /** A link prefetched on intent: one with no data-prefetch of its own. */
  #wantsIntent(link) {
    return !link.hasAttribute("data-prefetch") && this.#isCandidate(link);
  }

  #onPointerOver = (event) => {
    if (event.pointerType === "touch") return;
    const link = event.composedPath().find((node) => node instanceof HTMLAnchorElement) ?? null;
    if (link && link === this.#resting?.link) return;

    clearTimeout(this.#resting?.timer);
    this.#resting = null;
    if (!link || !this.#wantsIntent(link)) return;

    const timer = setTimeout(() => {
      this.#resting = null;
      this.#core.prefetch(link, { intent: true });
    }, HOVER_DELAY);
    this.#resting = { link, timer };
  };

  #onFocusIn = (event) => {
    const link = event.composedPath().find((node) => node instanceof HTMLAnchorElement);
    if (link && this.#wantsIntent(link)) this.#core.prefetch(link, { intent: true });
  };

  #onIntersect = (entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      this.#unobserve(entry.target);
      this.#queued.add(entry.target);
    }
    if (!this.#queued.size || this.#flushing) return;
    this.#flushing = true;
    requestIdleCallback(
      () => {
        this.#flushing = false;
        for (const link of this.#queued) if (link.isConnected) this.#core.prefetch(link);
        this.#queued.clear();
      },
      { timeout: 2000 }
    );
  };

  #unobserve(link) {
    this.#intersectionObserver.unobserve(link);
    this.#observed.delete(link);
  }
}

const start = (core) => {
  if (navigator.connection?.saveData) return;
  if (document.readyState === "complete") new LinkPrefetcher(core);
  else window.addEventListener("load", () => new LinkPrefetcher(core), { once: true });
};

if (window.__pjax) {
  start(window.__pjax);
} else {
  document.addEventListener("pjax:ready", (event) => start(event.detail.pjax), { once: true });
}
